import React from 'react';
import { ChevronRight, ChevronDown, Search as SearchIcon } from 'lucide-react';
import { Category } from '../../types';
import { useI18n } from '../../i18n';
import { useCategoryStore } from '../../stores/category-store';

export interface CategorySelectorProps {
  categories: Category[];
  value?: string | null;
  onChange: (id: string) => void;
  excludeIds?: Set<string>;
  className?: string;
  enableCreate?: boolean;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  categories,
  value,
  onChange,
  excludeIds,
  className,
  enableCreate = false,
}) => {
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
  const [search, setSearch] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [creatingError, setCreatingError] = React.useState<string | null>(null);
  const { createCategory } = useCategoryStore();

  const { t } = useI18n();
  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filterTreeByName = React.useCallback((nodes: Category[], keyword: string): Category[] => {
    if (!keyword.trim()) return nodes;
    const q = keyword.toLowerCase();
    const dfs = (arr: Category[]): Category[] =>
      arr
        .map((n) => ({ ...n, children: n.children ? dfs(n.children) : [] }))
        .filter((n) => (n.name || '').toLowerCase().includes(q) || (n.children && n.children.length > 0));
    return dfs(nodes);
  }, []);

  const NodeRow: React.FC<{ node: Category; depth: number }> = ({ node, depth }) => {
    const hasChildren = !!(node.children && node.children.length > 0);
    const disabled = excludeIds?.has(node.id) || false;
    return (
      <div className="w-full" key={node.id}>
        <div className="flex items-center" style={{ marginLeft: depth * 16 }}>
          {hasChildren ? (
            <button onClick={() => toggleExpand(node.id)} className="p-1 text-gray-500 hover:text-gray-700">
              {expandedIds.has(node.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          ) : (
            <span className="w-6" />
          )}
          <button
            disabled={disabled}
            onClick={() => onChange(node.id)}
            className={`flex-1 text-left px-2 py-1 rounded ${
              value === node.id ? 'bg-teal-50 text-teal-700' : 'hover:bg-gray-50'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {node.name}
          </button>
        </div>
        {expandedIds.has(node.id) && hasChildren && (
          <div className="">
            {node.children!.map((c) => (
              <NodeRow key={c.id} node={c as Category} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const filtered = React.useMemo(() => filterTreeByName(categories, search), [categories, search, filterTreeByName]);

  // 辅助：根据id找到节点
  const flatten = React.useCallback((nodes: Category[]): Category[] => {
    const res: Category[] = [] as any;
    const dfs = (arr: Category[]) => { for (const n of arr) { res.push(n); if (n.children) dfs(n.children as any); } };
    dfs(nodes);
    return res;
  }, []);

  const allFlat = React.useMemo(() => flatten(categories), [categories, flatten]);
  const getNode = (id?: string | null) => allFlat.find((n) => n.id === id);

  const siblingNames = React.useMemo(() => {
    const parentId = value || null;
    // 如果选择了某个节点，则在其下创建；否则在根创建
    if (!parentId) {
      return allFlat.filter((n) => (n as any).parent_id == null).map((n) => (n.name || '').toLowerCase().trim());
    }
    const parent = getNode(parentId);
    const children = parent?.children || [];
    return (children as any[]).map((n) => (n.name || '').toLowerCase().trim());
  }, [allFlat, value]);

  return (
    <div className={className}>
      <div className="relative mb-2">
        <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('searchFoldersPlaceholder')}
          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>
      {/** 创建新文件夹 */}
      {enableCreate && (
        <div className="mb-2">
          {!creating ? (
            <button
              onClick={() => { setCreating(true); setCreatingError(null); setNewName(''); }}
              className="px-3 py-1 text-xs border rounded-md hover:bg-gray-50"
            >{t('createFolder')}</button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t('folderNamePlaceholder')}
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
              <button
                onClick={async () => {
                  const trimmed = newName.trim();
                  if (!trimmed) { setCreatingError(t('nameRequired')); return; }
                  if (siblingNames.includes(trimmed.toLowerCase())) { setCreatingError(t('nameExists')); return; }
                  try {
                    const created = await createCategory(trimmed, value || undefined);
                    onChange(created.id);
                    setCreating(false);
                    setNewName('');
                    setCreatingError(null);
                  } catch (e:any) {
                    setCreatingError(e?.message || t('createFailed'));
                  }
                }}
                className="px-3 py-1 text-xs text-white bg-teal-600 rounded-md hover:bg-teal-700"
              >{t('save')}</button>
              <button onClick={() => setCreating(false)} className="px-3 py-1 text-xs border rounded-md">{t('cancel')}</button>
            </div>
          )}
          {creatingError && <div className="mt-1 text-xs text-red-600">{creatingError}</div>}
        </div>
      )}
      <div className="space-y-1 max-h-[45vh] overflow-y-auto">
        {filtered.map((node) => (
          <NodeRow key={node.id} node={node} depth={0} />
        ))}
      </div>
    </div>
  );
};

export default CategorySelector;


